import { useState } from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import { Button, Col, Descriptions, Form, Row, Upload } from 'antd';
import { Card } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { LoaderCircle } from 'lucide-react';

import FormField from '@/components/FormField';
import { statuses } from '@/config';
import getFormFields from '@/config/programs/compassionateFields';
import useGlobalConfig from '@/store/global';
import { formatDatePickerValue } from '@/utils/formatDate';

const DetailForm = ({ patient }: { patient: any }) => {
  const { themeColor, program } = useGlobalConfig();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const patientInfor = [
    { label: 'Patient First Name', value: patient.patient_first_name },
    { label: 'Patient Last Name', value: patient.patient_last_name },
    { label: 'Date of Birth', value: patient.patient_date_of_birth },
    { label: 'Patient Address Line', value: patient.patient_address_line1 },
    { label: 'Patient Address Line 2', value: patient.patient_address_line2 },
    { label: 'Patient City', value: patient.patient_city },
    { label: 'Patient State', value: patient.patient_state },
    { label: 'Patient ZIP', value: patient.patient_zip_code },
    { label: 'Patient Cell Number', value: patient.patient_cell_number },
    {
      label: 'Gross Monthly Household Income',
      value: patient.patient_gross_monthly_household_income,
    },
    {
      label: 'Number of People in Household (Including Self)',
      value: patient.patient_number_of_people_in_household,
    },
    { label: 'Current Diagnosis', value: patient.patient_current_diagnosis },
    { label: 'Current Product Used', value: patient.patient_current_product_used },
    { label: 'New Request/Renewal', value: patient.patient_new_request_renewal },
    { label: 'Product Name', value: patient.program_name },
    { label: "Licensed Practitioner's Signature", value: patient.licensed_prescriber_signature },
    { label: 'Date', value: patient.licensed_prescriber_signature_date },
  ];

  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const formFields = getFormFields(program?.name);

  const uploadProps = {
    multiple: false,
    maxCount: 1,
    beforeUpload: (file: any) => {
      setFileList([file]);
      return false;
    },
    fileList,
    onRemove: (file: any) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        try {
          setLoading(true);

          const formData = new FormData();
          Object.keys(values).forEach((key) => {
            if (key === 'prescription_document') {
              if (fileList[0] instanceof File) {
                formData.append('file', fileList[0]);
              }
            } else if (key === 'licensed_prescriber_signature_date') {
              formData.append(key, dayjs(new Date()).format('YYYY-MM-DD'));
            } else if (key === 'patient_signature_date') {
              formData.append(key, dayjs(new Date()).format('YYYY-MM-DD'));
            } else if (key === 'prescription_is_used') {
              formData.append(key, values[key] === true ? 'true' : 'false');
            } else {
              formData.append(key, values[key]);
            }
          });

          await axios.put(`compassionate_access_requests/${patient.id}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          form.resetFields();
          navigate('/requests/compassionate_access_requests');
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log('error: ', error);
      });
  };

  const status = statuses.find((status) => status.value === patient.status);
  const disabled = !['medmonk_submitted', 'cancelled'].includes(status?.value as string);

  useEffect(() => {
    form.setFieldsValue({
      ...patient,
      patient_signature_date: formatDatePickerValue(patient.patient_signature_date),
      licensed_prescriber_signature_date: formatDatePickerValue(
        patient.licensed_prescriber_signature_date
      ),
      patient_date_of_birth: formatDatePickerValue(patient.patient_date_of_birth),
    });

    if (patient?.prescription_document_url) {
      setFileList([
        {
          uid: '-1',
          name: 'Current prescription document',
          status: 'done',
          url: patient.prescription_document_url,
        },
      ]);
    } else {
      setFileList([]);
    }
  }, [patient]);

  return (
    <Card className="pt-4 border-t my-10">
      <div className="flex items-center mb-6">
        <div className="font-bold text-xl" style={{ color: themeColor }}>
          Compassionate Access Program - Request Form
        </div>
        <div className="ml-4 text-base">
          <span className={`${status?.color} text-white px-2 py-0.5 text-sm rounded`}>
            {status?.label}
          </span>
        </div>
      </div>

      <Descriptions title="Patient Information" column={2}>
        {patientInfor.map(({ label, value }) => (
          <Descriptions.Item key={label} label={label}>
            {value}
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Form form={form} onFinish={handleSubmit} layout="vertical" className="mt-10 bg-white">
        <div className="font-semibold text-lg">Prescriber Information</div>
        <Row gutter={[16, 16]} className="mt-4">
          {formFields.prescriberFields.map((field) => (
            <FormField disabled={disabled} key={field.name} field={field} />
          ))}
        </Row>

        <div className="mt-10 font-semibold text-lg">Prescription Information</div>
        <Row gutter={[16, 16]} className="mt-4">
          {formFields.prescriptionFields.map((field) => (
            <FormField disabled={disabled} key={field.name} field={field} />
          ))}
          <Col span={12}>
            <Form.Item required name="prescription_document" label="Prescription document">
              <Upload disabled={disabled} {...uploadProps}>
                <Button disabled={disabled}>Choose files</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <div className="mt-10 font-semibold text-lg">Signatures and Certification Statements</div>

        <div className="mt-8">
          I certify that I am a licensed practitioner eligible under state law to prescribe, receive
          and dispense the requested product listed on the request form. I agree the product will be
          provided by me to such eligible applicant for his or her use without charge. I will not
          otherwise use any of such product or prescribe, provide or dispense all or any portion
          thereof for the use of any other person.
        </div>
        <Row gutter={[16, 16]} className="mt-4">
          {formFields.licenseFields.map((field) => (
            <FormField disabled={disabled} key={field.name} field={field} />
          ))}
        </Row>
      </Form>
      <Button
        type="primary"
        size="large"
        loading={loading}
        disabled={loading || disabled}
        onClick={() => form.submit()}
        className={disabled ? 'opacity-50 w-full mb-6' : 'w-full mb-6'}
        style={{ backgroundColor: themeColor, borderColor: themeColor, color: 'white' }}
      >
        Update
      </Button>
    </Card>
  );
};

const NewForm = ({ patient }: { patient: any }) => {
  const { themeColor } = useGlobalConfig();
  const { program } = useGlobalConfig();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const patientInfor = [
    { label: 'Patient First Name', value: patient.patient_first_name },
    { label: 'Patient Last Name', value: patient.patient_last_name },
    { label: 'Date of Birth', value: patient.patient_date_of_birth },
    { label: 'Patient Address Line', value: patient.patient_address_line1 },
    { label: 'Patient Address Line 2', value: patient.patient_address_line2 },
    { label: 'Patient City', value: patient.patient_city },
    { label: 'Patient State', value: patient.patient_state },
    { label: 'Patient ZIP', value: patient.patient_zip_code },
    { label: 'Patient Cell Number', value: patient.patient_cell_number },
    {
      label: 'Gross Monthly Household Income',
      value: patient.patient_gross_monthly_household_income,
    },
    {
      label: 'Number of People in Household (Including Self)',
      value: patient.patient_number_of_people_in_household,
    },
    { label: 'Current Diagnosis', value: patient.patient_current_diagnosis },
    { label: 'Current Product Used', value: patient.patient_current_product_used },
    { label: 'New Request/Renewal', value: patient.patient_new_request_renewal },
    { label: 'Product Name', value: patient.program_name },
  ];

  console.log('patient: ', patient);
  const [fileList, setFileList] = useState<any[]>([]);
  const uploadProps = {
    multiple: false,
    maxCount: 1,
    beforeUpload: (file: any) => {
      setFileList([file]);
      return false;
    },
    fileList,
    onRemove: (file: any) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        try {
          setLoading(true);

          const formData = new FormData();
          Object.keys(values).forEach((key) => {
            if (key === 'prescription_document') {
              formData.append('file', fileList[0]);
            } else if (key === 'licensed_prescriber_signature_date') {
              formData.append(key, dayjs(new Date()).format('YYYY-MM-DD'));
            } else if (key === 'patient_signature_date') {
              formData.append(key, dayjs(new Date()).format('YYYY-MM-DD'));
            } else if (key === 'prescription_is_used') {
              formData.append(key, values[key] === true ? 'true' : 'false');
            } else {
              formData.append(key, values[key]);
            }
          });

          await axios.post(`compassionate_access_requests/${patient.id}/submit`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          form.resetFields();
          navigate('/requests/compassionate_access_requests');
        } catch (error) {
          console.error(error);
        } finally {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log('error: ', error);
      });
  };

  const status = statuses.find((status) => status.value === patient.status);
  const formFields = getFormFields(program?.name);

  return (
    <Card className="pt-4 border-t mb-10">
      <div className="flex items-center mb-6">
        <div className="font-bold text-xl" style={{ color: themeColor }}>
          Compassionate Access Program - Request Form
        </div>
        <div className="ml-4 text-base">
          <span className={`${status?.color} text-white px-2 py-0.5 text-sm rounded`}>
            {status?.label}
          </span>
        </div>
      </div>
      <Descriptions title="Patient Information" column={2}>
        {patientInfor.map(({ label, value }) => (
          <Descriptions.Item key={label} label={label}>
            {value}
          </Descriptions.Item>
        ))}
      </Descriptions>

      <Form form={form} onFinish={handleSubmit} layout="vertical" className="mt-10 bg-white">
        <div className="font-semibold text-lg">Prescriber Information</div>
        <Row gutter={[16, 16]} className="mt-4">
          {formFields.prescriberFields.map((field) => (
            <FormField key={field.name} field={field} />
          ))}
        </Row>

        <div className="mt-10 font-semibold text-lg">Prescription Information</div>
        <Row gutter={[16, 16]} className="mt-4">
          {formFields.prescriptionFields.map((field) => (
            <FormField key={field.name} field={field} />
          ))}
          <Col span={12}>
            <Form.Item required name="prescription_document" label="Prescription document">
              <Upload {...uploadProps}>
                <Button>Choose files</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <div className="mt-10 font-semibold text-lg">Signatures and Certification Statements</div>

        <div className="mt-8">
          I certify that I am a licensed practitioner eligible under state law to prescribe, receive
          and dispense the requested product listed on the request form. I agree the product will be
          provided by me to such eligible applicant for his or her use without charge. I will not
          otherwise use any of such product or prescribe, provide or dispense all or any portion
          thereof for the use of any other person.
        </div>
        <Row gutter={[16, 16]} className="mt-4">
          {formFields.licenseFields.map((field) => (
            <FormField key={field.name} field={field} />
          ))}
        </Row>
      </Form>
      <Button
        className="w-full mb-6 "
        type="primary"
        size="large"
        loading={loading}
        disabled={loading}
        onClick={() => form.submit()}
        style={{ backgroundColor: themeColor, borderColor: themeColor, color: 'white' }}
      >
        Submit
      </Button>
    </Card>
  );
};

const CompassionateRequestDetail = () => {
  const { compassionateRequestId } = useParams();
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const loadAssistanceRequestData = async () => {
    try {
      const { data } = await axios.get(`compassionate_access_requests/${compassionateRequestId}`);
      setData(data);
    } catch (error: any) {
      if (error.status === 404) {
        navigate('/not-found');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssistanceRequestData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <LoaderCircle className="animate-spin w-10 h-10" />
      </div>
    );
  }

  return data.status === 'medmonk_submitted' ? (
    <NewForm patient={data} />
  ) : (
    <DetailForm patient={data} />
  );
};

export default CompassionateRequestDetail;
